import { useState } from "react";
import MobileAppLayout from "@/components/app/MobileAppLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tag, Trash2, Plus } from "lucide-react";
import { useAppMode } from "@/hooks/useAppMode";
import { useUserCoupons, useLinkCoupon, useRemoveCoupon } from "@/hooks/useUserCoupons";

const AppCoupons = () => {
  const { provider } = useAppMode();
  const [couponCode, setCouponCode] = useState("");

  const { data: userCoupons = [], isLoading } = useUserCoupons();
  const linkCoupon = useLinkCoupon();
  const removeCoupon = useRemoveCoupon();

  // Filter coupons to the current provider only
  const providerCoupons = provider
    ? userCoupons.filter((uc) => uc.provider_id === provider.id)
    : userCoupons;

  const handleLink = async () => {
    if (!couponCode.trim()) return;
    try {
      await linkCoupon.mutateAsync(couponCode.trim());
      setCouponCode("");
    } catch (e) {
      // error toast handled by hook
    }
  };

  const formatDiscount = (c: { discount_type: string; discount_value: number; credits_per_period?: number }) => {
    if (c.discount_type === "percentage") return `${c.discount_value}% off`;
    if (c.discount_type === "fixed") return `£${c.discount_value} off`;
    if (c.discount_type === "weekly_credits") return `${c.credits_per_period ?? 0} credits / week`;
    if (c.discount_type === "monthly_credits") return `${c.credits_per_period ?? 0} credits / month`;
    return "";
  };

  return (
    <MobileAppLayout title="Coupons">
      <div className="px-4 py-5 space-y-5">
        {/* Add coupon */}
        <Card>
          <CardContent className="p-4 space-y-3">
            <p className="text-sm font-medium">Add a coupon code</p>
            <div className="flex gap-2">
              <Input
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                placeholder="ENTER CODE"
                className="uppercase"
              />
              <Button
                onClick={handleLink}
                disabled={!couponCode.trim() || linkCoupon.isPending}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Linked coupons */}
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-2 px-1">
            Your coupons
          </h2>

          {isLoading ? (
            <Card>
              <CardContent className="p-4 text-sm text-muted-foreground">
                Loading…
              </CardContent>
            </Card>
          ) : providerCoupons.length === 0 ? (
            <Card>
              <CardContent className="p-6 text-center text-sm text-muted-foreground">
                <Tag className="h-8 w-8 mx-auto mb-2 opacity-50" />
                No coupons linked yet.
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {providerCoupons.map((uc) => (
                <Card key={uc.id}>
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                      <Tag className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{uc.coupon.name}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {uc.coupon.code} · {formatDiscount(uc.coupon)}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeCoupon.mutate(uc.id)}
                      disabled={removeCoupon.isPending}
                      aria-label="Remove coupon"
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </MobileAppLayout>
  );
};

export default AppCoupons;
